import React from "react"

import type { AudioRegionReviewConfig } from "@/metrics/runtime"

import type { AudioRegionParams } from "@/components/audio-player/AudioPlayer"
import type {
  MetricOutputDisplaySource,
  RunMetricOutput,
} from "@/types/RunMetricOutput"

import { NumericalMetricRecord } from "../base/NumericalMetricRecord"
import { AnomalyEventList } from "../components/AnomalyEventList"
import { getCardStyle } from "../components/deepDiveCardStyles"
import { WithAudioGateMixin } from "../mixins/WithAudioGateMixin"

const CATEGORY = "clipping_artifact"
const SEVERITY_THRESHOLD = 0.5

interface ClippingArtifactResultJson {
  clipping_total_duration_seconds?: number
  clipping_sample_fraction?: number
  clipping_event_count?: number
}

export class ClippingArtifactMetricRecord extends WithAudioGateMixin(
  NumericalMetricRecord,
) {
  unit = ""
  aggregate_units = ""

  _renderDeepDive(mo: RunMetricOutput): React.ReactNode {
    const resultJson = (mo.result as Record<string, unknown> | undefined)
      ?.raw_values as ClippingArtifactResultJson | undefined
    const fraction = resultJson?.clipping_sample_fraction ?? mo.float_value ?? 0
    const eventCount = resultJson?.clipping_event_count ?? 0
    const totalDuration = resultJson?.clipping_total_duration_seconds ?? 0
    const subvalues = mo.subvalues_by_timestamp ?? []
    const card = getCardStyle(fraction, 0.001)

    return (
      <div className="space-y-4">
        <div className="grid grid-cols-3 gap-3">
          <div className={`rounded-lg p-3 ${card.bg}`}>
            <div className={card.label}>Clipping Fraction</div>
            <div className={card.value}>{(fraction * 100).toFixed(2)}%</div>
          </div>
          <div className={`rounded-lg p-3 ${card.bg}`}>
            <div className={card.label}>Events</div>
            <div className={card.value}>{eventCount}</div>
          </div>
          <div className={`rounded-lg p-3 ${card.bg}`}>
            <div className={card.label}>Total Duration</div>
            <div className={card.value}>{totalDuration.toFixed(2)}s</div>
          </div>
        </div>

        <AnomalyEventList
          subvalues={subvalues}
          severityThreshold={SEVERITY_THRESHOLD}
          chipLabel="Clipping"
          title="Clipping Segments"
        />

        {subvalues.length === 0 && (
          <div className="text-sm text-coval-text-muted">
            No clipping artifacts detected
          </div>
        )}
      </div>
    )
  }

  getAudioRegions(mo: MetricOutputDisplaySource): AudioRegionParams[] {
    if (mo?.error_message) {
      return []
    }
    try {
      const subvalues = mo.subvalues_by_timestamp ?? []
      return subvalues
        .filter((item) => (item.float_value ?? 0) >= SEVERITY_THRESHOLD)
        .map((item, idx) => {
          const severity = item.float_value ?? 0
          const alpha = Math.min(0.2 + severity * 0.5, 0.7)
          return {
            id: `${CATEGORY}:${idx}`,
            start: item.start_offset,
            end: Math.max(item.end_offset, item.start_offset + 0.1),
            color: `rgba(147, 51, 234, ${alpha.toFixed(2)})`,
            drag: false,
            resize: false,
          }
        })
    } catch {
      return []
    }
  }

  getAudioRegionReviewConfig(): AudioRegionReviewConfig | null {
    return {
      category: CATEGORY,
      scoreType: "raw_count",
      buildReviewedResultJson: ({ score }) => ({
        clipping_event_count: Number(score.toFixed(0)),
      }),
    }
  }

  renderMetricType(): React.ReactNode {
    return "Clipping Artifact Detection"
  }
}
